import { Close as CloseIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { camelCaseToSentenceCase } from '../../../../shared/utils';

export interface PriceOverrideFormData {
    unitPrice: string;
    nightPrice: string;
    dayPrice: string;
    hourPrice: string;
    desk: string;
    deskHalfDay: string;
    privateOffice: string;
    privateOfficeHalfDay: string;
    meetingRoom: string;
    meetingRoomHalfDay: string;
    entireVenue: string;
    entireVenueHalfDay: string;
}

interface PriceOverrideModalProps {
    open: boolean;
    onClose: () => void;
    selectedDate: Date | null;
    selectedDatesCount?: number;
    isFlexiblePrice: boolean;
    onSave: (override: PriceOverrideFormData) => void;
    onDelete?: () => void;
    experienceType: string;
    shouldShowHalfDayBilling?: boolean;
}

type CommercialField = {
    key: keyof PriceOverrideFormData;
    halfDayKey: keyof PriceOverrideFormData;
    label: string;
};

const COMMERCIAL_FIELDS: CommercialField[] = [
    { key: 'desk', halfDayKey: 'deskHalfDay', label: 'Desk' },
    {
        key: 'privateOffice',
        halfDayKey: 'privateOfficeHalfDay',
        label: 'Private Office',
    },
    {
        key: 'meetingRoom',
        halfDayKey: 'meetingRoomHalfDay',
        label: 'Meeting Room',
    },
    {
        key: 'entireVenue',
        halfDayKey: 'entireVenueHalfDay',
        label: 'Entire Venue',
    },
];

const PriceOverrideModal: React.FC<PriceOverrideModalProps> = ({
    open,
    onClose,
    selectedDate,
    selectedDatesCount = 1,
    isFlexiblePrice,
    onSave,
    onDelete,
    experienceType,
    shouldShowHalfDayBilling = false,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        getValues,
        formState: { isDirty },
    } = useForm<PriceOverrideFormData>({
        mode: 'onChange',
        defaultValues: {
            unitPrice: '',
            nightPrice: '',
            dayPrice: '',
            hourPrice: '',
            desk: '',
            deskHalfDay: '',
            privateOffice: '',
            privateOfficeHalfDay: '',
            meetingRoom: '',
            meetingRoomHalfDay: '',
            entireVenue: '',
            entireVenueHalfDay: '',
        },
    });

    const isCommercial = experienceType === 'commercial';

    const flexibleFields = [
        { name: 'dayPrice' as const },
        { name: 'nightPrice' as const },
        { name: 'hourPrice' as const },
    ];

    const onSubmit = (data: PriceOverrideFormData) => {
        onSave(data);
        handleClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleDeleteOverride = () => {
        if (onDelete) {
            onDelete();
        }
        handleClose();
    };

    const renderPriceField = (
        name: keyof PriceOverrideFormData,
        relevantKeys: (keyof PriceOverrideFormData)[],
        label?: string,
        endLabel?: string,
    ) => (
        <Controller
            key={name}
            name={name}
            control={control}
            // rules={{
            //     validate: (value) =>
            //         validateAtLeastOnePrice(value, relevantKeys),
            // }}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    label={label ?? null}
                    type='number'
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>$</InputAdornment>
                        ),
                        endAdornment: endLabel ? (
                            <InputAdornment position='end'>
                                <Typography sx={{ fontSize: '14px' }}>
                                    {endLabel}
                                </Typography>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    fullWidth
                    size='small'
                />
            )}
        />
    );

    const commercialRelevantKeys: (keyof PriceOverrideFormData)[] =
        COMMERCIAL_FIELDS.flatMap((f) =>
            shouldShowHalfDayBilling ? [f.key, f.halfDayKey] : [f.key],
        );

    const flexibleRelevantKeys: (keyof PriceOverrideFormData)[] = [
        'dayPrice',
        'nightPrice',
        'hourPrice',
    ];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Set Price Override
                <IconButton onClick={handleClose} size='small'>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {selectedDatesCount > 1 ? (
                        <Alert severity='info'>
                            Setting custom prices for{' '}
                            <strong>{selectedDatesCount} selected dates</strong>
                        </Alert>
                    ) : selectedDate ? (
                        <Alert severity='info'>
                            Setting custom price for{' '}
                            <strong>
                                {format(selectedDate, 'MMMM d, yyyy')}
                            </strong>
                        </Alert>
                    ) : null}

                    {isCommercial ? (
                        <Grid container item rowSpacing={2}>
                            {COMMERCIAL_FIELDS.map((item) => (
                                <Grid
                                    container
                                    item
                                    spacing={2}
                                    alignItems='center'
                                    key={item.key}
                                >
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel>{item.label}</InputLabel>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={shouldShowHalfDayBilling ? 4.5 : 9}
                                    >
                                        {renderPriceField(
                                            item.key,
                                            commercialRelevantKeys,
                                            undefined,
                                            shouldShowHalfDayBilling
                                                ? 'Per Day'
                                                : undefined,
                                        )}
                                    </Grid>
                                    {shouldShowHalfDayBilling && (
                                        <Grid item xs={12} sm={4.5}>
                                            {renderPriceField(
                                                item.halfDayKey,
                                                commercialRelevantKeys,
                                                undefined,
                                                'Per Half Day',
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    ) : isFlexiblePrice ? (
                        flexibleFields.map((item) =>
                            renderPriceField(
                                item.name,
                                flexibleRelevantKeys,
                                camelCaseToSentenceCase(item.name),
                            ),
                        )
                    ) : (
                        renderPriceField(
                            'unitPrice',
                            ['unitPrice'],
                            'Unit Price',
                        )
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}
            >
                <Box>
                    {selectedDatesCount > 1 && (
                        <Button onClick={handleDeleteOverride} color='error'>
                            {selectedDatesCount === 1
                                ? `Remove Override`
                                : `Remove Overrides for Selected`}
                        </Button>
                    )}
                </Box>
                <Box display='flex' gap={1}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        variant='contained'
                        disabled={!isDirty}
                    >
                        {selectedDatesCount > 1 ? 'Apply to All' : 'Save'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PriceOverrideModal;
